'use client'
import React, { useState, useEffect } from 'react'
import { Search, Eye, Users, Fingerprint, AlertTriangle, CheckCircle, XCircle, Calendar, DollarSign } from 'lucide-react'
import { socket } from '../Containers/MainContainer'

function PlutoFingerDet({ second_page }) {
  const [users, setUsers] = useState([])
  const [rank, setRank] = useState(0)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('id')
  const [sortOrder, setSortOrder] = useState('asc')

  // Mock socket for demo - replace with your actual socket
 
  useEffect(() => {
    socket.on('response_get_admin_fpdetails_users', (data) => {
      setUsers(data.users)
      setRank(data.rank)
      setLoading(false)
    })

    socket.emit('getFPDetailsAdmin', {
      fp: second_page,
    })
  }, [second_page])

  // Filter and sort users
  const filteredUsers = users.filter(user =>
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.id?.toString().includes(searchTerm) ||
    user.country?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let aVal = a[sortBy]
    let bVal = b[sortBy]
    
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase()
      bVal = bVal.toLowerCase()
    }
    
    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : -1
    } else {
      return aVal < bVal ? 1 : -1
    }
  })

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(column)
      setSortOrder('asc')
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getBannedStatus = (banned) => {
    if (banned === 'True') {
      return (
        <div className="flex items-center gap-1 text-red-400">
          <XCircle className="h-4 w-4" />
          <span>Banned</span>
        </div>
      )
    } else {
      return (
        <div className="flex items-center gap-1 text-green-400">
          <CheckCircle className="h-4 w-4" />
          <span>Active</span>
        </div>
      )
    }
  }

  const StatCard = ({ icon: Icon, title, value, color = "blue" }) => (
    <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg bg-${color}-500/20`}>
          <Icon className={`h-5 w-5 text-${color}-400`} />
        </div>
        <div>
          <div className="text-sm font-medium text-gray-400">{title}</div>
          <div className="text-xl font-bold text-gray-200">{value}</div>
        </div>
      </div>
    </div>
  )

  const LoadingSkeleton = () => (
    <div className="space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="bg-gray-800 rounded-lg p-4 animate-pulse">
          <div className="grid grid-cols-8 gap-4">
            <div className="h-4 bg-gray-700 rounded"></div>
            <div className="h-4 bg-gray-700 rounded"></div>
            <div className="h-4 bg-gray-700 rounded"></div>
            <div className="h-4 bg-gray-700 rounded"></div>
            <div className="h-4 bg-gray-700 rounded"></div>
            <div className="h-4 bg-gray-700 rounded"></div>
            <div className="h-4 bg-gray-700 rounded"></div>
            <div className="h-4 bg-gray-700 rounded"></div>
          </div>
        </div>
      ))}
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto p-4 lg:p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Fingerprint className="h-8 w-8 text-blue-400" />
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-200">
              Fingerprint Details
            </h1>
          </div>
          <p className="text-gray-400">
            Users associated with fingerprint: <span className="text-blue-400 font-mono">{second_page}</span>
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <StatCard 
            icon={Users} 
            title="Total Users" 
            value={users.length}
            color="blue" 
          />
          <StatCard 
            icon={CheckCircle} 
            title="Active Users" 
            value={users.filter(u => u.banned === 'False').length}
            color="green" 
          />
          <StatCard 
            icon={XCircle} 
            title="Banned Users" 
            value={users.filter(u => u.banned === 'True').length}
            color="red" 
          />
          <StatCard 
            icon={DollarSign} 
            title="Total Earned" 
            value={formatCurrency(users.reduce((sum, u) => sum + (parseFloat(u.total_earned) || 0), 0))}
            color="purple" 
          />
        </div>

        {/* Search and Filters */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 mb-6">
          <div className="p-4 border-b border-gray-700">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by ID, username, or country..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full bg-gray-900 text-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 border border-gray-600"
                />
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <span>Showing {sortedUsers.length} of {users.length} users</span>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="p-4">
                <LoadingSkeleton />
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-900">
                  <tr>
                    {[
                      { key: 'id', label: 'ID' },
                      { key: 'username', label: 'Username' },
                      { key: 'country', label: 'Country' },
                      { key: 'joinDate', label: 'Join Date' },
                      { key: 'balance', label: 'Balance' },
                      { key: 'total_earned', label: 'Total Earned' },
                      { key: 'banned', label: 'Status' },
                      { key: 'actions', label: 'Actions' }
                    ].map((column) => (
                      <th
                        key={column.key}
                        className={`px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider ${
                          column.key !== 'actions' ? 'cursor-pointer hover:text-gray-300' : ''
                        }`}
                        onClick={() => column.key !== 'actions' && handleSort(column.key)}
                      >
                        <div className="flex items-center gap-1">
                          {column.label}
                          {sortBy === column.key && (
                            <span className="text-blue-400">
                              {sortOrder === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {sortedUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-700/20 transition-colors">
                      <td className="px-4 py-3 text-sm font-mono text-gray-300">
                        #{user.id}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-gray-200">{user.username}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-300">
                        <div className="flex items-center gap-2">
                          <span className="text-xs bg-gray-700 px-2 py-1 rounded">
                            {user.country}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-300">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(user.joinDate)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-green-400">
                        {formatCurrency(user.balance)}
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-blue-400">
                        {formatCurrency(user.total_earned)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {getBannedStatus(user.banned)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <a
                          href={`/pluto/user/${user.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors"
                        >
                          <Eye className="h-3 w-3" />
                          View User
                        </a>
                      </td>
                    </tr>
                  ))}
                  {sortedUsers.length === 0 && !loading && (
                    <tr>
                      <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                        No users found matching your search criteria
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Summary */}
        {!loading && users.length > 0 && (
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <h3 className="text-lg font-semibold text-gray-200 mb-3 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-400" />
              Fingerprint Analysis Summary
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-400">Countries detected:</span>
                <span className="ml-2 text-gray-200 font-medium">
                  {[...new Set(users.map(u => u.country))].length} 
                  {" "}({[...new Set(users.map(u => u.country))].join(', ')})
                </span>
              </div>
              <div>
                <span className="text-gray-400">Registration span:</span>
                <span className="ml-2 text-gray-200 font-medium">
                  {users.length > 0 && 
                    `${Math.ceil((new Date(Math.max(...users.map(u => new Date(u.joinDate)))) - 
                      new Date(Math.min(...users.map(u => new Date(u.joinDate))))) / (1000 * 60 * 60 * 24))} days`
                  }
                </span>
              </div>
              <div>
                <span className="text-gray-400">Avg. earnings:</span>
                <span className="ml-2 text-gray-200 font-medium">
                  {formatCurrency(users.reduce((sum, u) => sum + (parseFloat(u.total_earned) || 0), 0) / users.length)}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PlutoFingerDet